import { ReactComponent as IconDocumentLine } from "@/assets/svgs/document-line.svg";
import CheckboxButton from "@/components/CheckboxButton/index";
import useConfig from "@/hooks/use-config";
import useEtr from "@/hooks/use-etr/index";
import useStaticData from "@/hooks/use-static-data/index";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import AddressDropdown from "./components/address-dropdown";
import NetworkDropdown from "./components/network-dropdown";
import TokenRegistryDropdown from "./components/token-registry-version";
import RemarksComponent from "./components/remarks-component";
import { feature } from "@/utils/feature-verifier.js";
import { featureConstant } from "@/constants/configuration/feature.js";
import tokenRegistryVersions from "@/constants/token-registry-versions/tokenRegistryVersion";

const RecordDetailsSection = ({
	ownerAddress,
	setOwnerAddress,
	holderAddress,
	setHolderAddress,
	selectedNetwork,
	setSelectedNetwork,
	tokenRegistryVersion,
	setTokenRegistryVersion,
	remarks,
	setRemarks,
	isSameOwnerHolder,
	setSameOwnerHolder,
	isSameOwnerHolderDisabled,
}) => {
	// Owner and Holder Address Dropdown lists
	const [combineAddress, setCombineAddress] = useState([]);
	const dataElementId = "ebl";

	const isETRIGPNITRV5FeatureEnabled = feature(featureConstant?.ETR_IGPNI_TR_V5);
	const [filterTokenRegistryVersion, setFilterTokenRegistryVersion] = useState();

	const { identity, provideItems } = useConfig();
	const { fetchEtr, etrData } = useEtr({
		dataElementId,
		configData: {
			provideItems,
			identity,
		},
	});

	useEffect(() => {
		if (provideItems && identity) {
			fetchEtr();
		}
	}, [provideItems, identity]);

	useEffect(() => {
		setCombineAddress(etrData);
	}, [etrData]);

	// Network Dropdown lists
	const [networkOptions, setNetworkOptions] = useState([]);
	const { contracts } = useStaticData() ?? { contracts: [] };

	useEffect(() => {
		if (tokenRegistryVersion?.id != "V5") {
			setRemarks("");
		}
	}, [tokenRegistryVersion]);

	useEffect(() => {
		const groupedNetworks = contracts.reduce((acc, network) => {
			const { networkId, tokenRegistryVersion } = network;
			if (!acc[networkId]) {
				acc[networkId] = {
					...network,
					tokenRegistryVersion: [tokenRegistryVersion],
				};
			} else {
				acc[networkId].tokenRegistryVersion.push(tokenRegistryVersion);
			}
			return acc;
		}, {});

		const filteredNetworks = Object.values(groupedNetworks);
		setNetworkOptions(filteredNetworks);
	}, [contracts?.length]);

	useEffect(() => {
		if (!selectedNetwork) return;
		setFilterTokenRegistryVersion((prev) => {
			if (prev === selectedNetwork?.tokenRegistryVersion) return prev;
			return selectedNetwork?.tokenRegistryVersion;
		});
		if (selectedNetwork?.tokenRegistryVersion?.length === 1) {
			const selectedTokenVersion = tokenRegistryVersions.find((version) => version.id === selectedNetwork?.tokenRegistryVersion[0]);
			setTokenRegistryVersion((prev) => {
				if (prev?.id === selectedTokenVersion?.id) return prev;
				return selectedTokenVersion;
			});
		} else if (selectedNetwork?.tokenRegistryVersion?.length === tokenRegistryVersions?.length) {
			const latestVersion = tokenRegistryVersions.reduce((latest, current) => { return current.id > latest.id ? current : latest }, tokenRegistryVersions[0]);
			setTokenRegistryVersion((prev) => {
				if (prev?.id === latestVersion?.id) return prev;
				return latestVersion;
			});
		} else {
			setTokenRegistryVersion((prev) => {
				if (prev === null) return prev;
				return null;
			});
		}
		setRemarks((prev) => (prev?.length > 0 ? prev : ""));
	}, [selectedNetwork]);

	return (
		<div className="etr__record-details__section">
			<div className="etr__record-details__section__header">
				<div className="etr__record-details__section__icon">
					<IconDocumentLine />
				</div>
				<div className="etr__record-details__section__heading">
					Record Details
				</div>
			</div>
			<div className="etr__record-details__section__content">
				<div className="etr__record-details__address__header">
					Addresses
				</div>
				<div className="etr__record-details__section__content__grid">
					<div className="etr__record-details__same__ownerholder__checkbox">
						<CheckboxButton
							submit={setSameOwnerHolder}
							disabled={isSameOwnerHolderDisabled}
							check={isSameOwnerHolder}
							name={"Owner and holder share the same address"}
						/>
					</div>
					<AddressDropdown
						title="Owner Address"
						selectedAddress={ownerAddress}
						setSelectedAddress={setOwnerAddress}
						availableAddresses={combineAddress}
					/>
					<AddressDropdown
						title="Holder Address"
						selectedAddress={holderAddress}
						setSelectedAddress={setHolderAddress}
						availableAddresses={combineAddress}
					/>
				</div>
				<div className="etr__record-details__blockchain__header">
					Blockchain
				</div>
				<div className="etr__record-details__section__blockchain__grid">
					<NetworkDropdown
						selectedNetwork={selectedNetwork}
						setSelectedNetwork={setSelectedNetwork}
						availableNetworks={networkOptions.filter((network) => network.contractAddress && network.isEnabled)}
					/>
					{isETRIGPNITRV5FeatureEnabled ? (
						<TokenRegistryDropdown
							tokenRegistryVersion={tokenRegistryVersion}
							setTokenRegistryVersion={setTokenRegistryVersion}
							availableTokenVersions={tokenRegistryVersions.filter((tokenVersion) => filterTokenRegistryVersion?.includes(tokenVersion.id))}
							selectedNetwork={selectedNetwork}
							showDropdown={true}
							showMessage={tokenRegistryVersions?.length !== filterTokenRegistryVersion?.length}
						/>
					) : <div className="etr__record-details__section__content__grid__item" />}
				</div>
				{isETRIGPNITRV5FeatureEnabled && tokenRegistryVersion?.id === "V5" &&
					<RemarksComponent
						remarks={remarks}
						setRemarks={setRemarks}
						showTextArea={true}
					/>
				}
			</div>
		</div>
	);
};

RecordDetailsSection.propTypes = {
	ownerAddress: PropTypes.object,
	setOwnerAddress: PropTypes.func,
	holderAddress: PropTypes.object,
	setHolderAddress: PropTypes.func,
	selectedNetwork: PropTypes.object,
	setSelectedNetwork: PropTypes.func,
	setSameOwnerHolder: PropTypes.func,
	tokenRegistryVersion: PropTypes.string,
	setTokenRegistryVersion: PropTypes.func,
	remarks: PropTypes.string,
	setRemarks: PropTypes.func,
	isSameOwnerHolder: PropTypes.bool,
	isSameOwnerHolderDisabled: PropTypes.bool,
};

export default RecordDetailsSection;
